import { PublicConfig } from "../PublicConfig";
import { Option } from "../types/Quote.interface";
import { PetUnderwriterType } from "@spotpetinsurance/spot-types/entities/PetQuote";

/**
 * Whether a market's API returns accurate per-coverage waiting-period dates.
 *
 * Single source of truth for hiding waiting-period dates everywhere they surface:
 * the Thank You page's PolicyDatesTracker and the welcome PDF's coverage start dates.
 * When false, those dates are hidden to avoid showing computed/placeholder dates that
 * would always be wrong.
 *
 * Canada does not yet return real per-coverage dates — flip "ptz-ca" to true (in this
 * one place) once it does, and both the tracker and the PDF will start showing them.
 */
export const WAITING_PERIOD_DATES_ENABLED: Record<PetUnderwriterType, boolean> = {
    "ptz-us": true,
    "ptz-ca": false
};

export interface State {
    label: string;
    value: string;
}

export const MAX_PET_NAME_LABEL = 20;
export const MAX_PET_NAME = 50;
export const MAX_USER_NAME = 50;

export const DEFAULT_REIMBURSEMENT_RATE = 70;
export const DEFAULT_ANNUAL_DEDUCTIBLE = 500;
export const DEFAULT_ANNUAL_LIMIT = 2500;

export const TMP_POLICY_PREFIX = "tmp:";

interface QuoteVersionOption extends Option {
    formId: string;
}

export const QUOTE_VERSIONS: QuoteVersionOption[] = [
    {
        formId: PublicConfig.PTZ_US.FORM_ID,
        label: "US",
        value: "forms/quote-ptz-us"
    },
    {
        formId: PublicConfig.PTZ_CA.FORM_ID,
        label: "CA",
        value: "forms/quote-ptz-ca"
    },
    {
        formId: PublicConfig.PTZ_US_FALLBACK.FORM_ID,
        label: "Fallback",
        value: "forms/quote-fallback-us"
    },
    {
        formId: PublicConfig.PTZ_US_RETARGET.FORM_ID,
        label: "Retarget",
        value: "forms/quote-retarget-us"
    },
    {
        formId: PublicConfig.UPDATE_BILLING.FORM_ID,
        label: "Update billing",
        value: "forms/update-billing"
    },
    {
        formId: PublicConfig.SAMS_LOGIN.FORM_ID,
        label: "Sam’s Club - Login",
        value: "forms/sams"
    },
    {
        formId: "sams-quote",
        label: "Sam's Club - Quote",
        value: "/"
    },
    {
        formId: PublicConfig.ADD_A_PET.FORM_ID,
        label: "Add a Pet",
        value: "forms/add-a-pet"
    }
];

export const LABEL_VARIANTS: Option[] = [
    {
        label: "Default",
        value: "default"
    },
    {
        label: "Floating",
        value: "floating"
    }
];

// The five generic "size" mixes, which every state offers under a name of its own. The size word
// and the weight range are the only stable parts, so anything keyed on these breeds has to match
// by shape rather than by literal name (ENG-17827 — IPG renamed CA's generic mixes, e.g.
// "Medium Mix (26-50 pounds)" → "Medium Mix (26-50 lbs.)" and "Small Mix (up to 10 pounds)" →
// "Toy Mix (up to 12 lbs.)"). Single source of truth: dropdown pinning (TOP_BREED_PATTERNS) and
// size-band/label resolution (BreedUtils) both read this one pattern, so the next IPG rename is
// handled in exactly one place.
//
//   50 states + DC   Small Mix (up to 10 pounds) / Small Mix (11-25 pounds) / Medium Mix (26-50 pounds) / ...
//   CA               Toy Mix (up to 12 lbs.)     / Small Mix (13-25 lbs.)   / Medium Mix (26-50 lbs.)   / ...
export const GENERIC_MIX_PATTERN = /^(Giant|Large|Medium|Small|Toy)\s+Mix\s*\(([^)]*)\)\s*$/i;

// Top-of-dropdown breeds are matched by REGEX, not exact name: exact matching silently stops
// pinning them to the top when a state's vocabulary shifts (see GENERIC_MIX_PATTERN above).
export const TOP_BREED_PATTERNS: RegExp[] = [GENERIC_MIX_PATTERN];
// Canada (ptz-ca) vocabulary: "Mixed Breed, Large (over 44 lbs fully grown)" etc.
export const TOP_BREED_PATTERNS_CA: RegExp[] = [/^Mixed Breed, (Large|Medium|Small)\s*\(/i];

export const US_STATES: State[] = [
    {
        label: "Alabama",
        value: "AL"
    },
    {
        label: "Alaska",
        value: "AK"
    },
    {
        label: "American Samoa",
        value: "AS"
    },
    {
        label: "Arizona",
        value: "AZ"
    },
    {
        label: "Arkansas",
        value: "AR"
    },
    {
        label: "California",
        value: "CA"
    },
    {
        label: "Colorado",
        value: "CO"
    },
    {
        label: "Connecticut",
        value: "CT"
    },
    {
        label: "Delaware",
        value: "DE"
    },
    {
        label: "District Of Columbia",
        value: "DC"
    },
    {
        label: "Federated States Of Micronesia",
        value: "FM"
    },
    {
        label: "Florida",
        value: "FL"
    },
    {
        label: "Georgia",
        value: "GA"
    },
    {
        label: "Guam",
        value: "GU"
    },
    {
        label: "Hawaii",
        value: "HI"
    },
    {
        label: "Idaho",
        value: "ID"
    },
    {
        label: "Illinois",
        value: "IL"
    },
    {
        label: "Indiana",
        value: "IN"
    },
    {
        label: "Iowa",
        value: "IA"
    },
    {
        label: "Kansas",
        value: "KS"
    },
    {
        label: "Kentucky",
        value: "KY"
    },
    {
        label: "Louisiana",
        value: "LA"
    },
    {
        label: "Maine",
        value: "ME"
    },
    {
        label: "Marshall Islands",
        value: "MH"
    },
    {
        label: "Maryland",
        value: "MD"
    },
    {
        label: "Massachusetts",
        value: "MA"
    },
    {
        label: "Michigan",
        value: "MI"
    },
    {
        label: "Minnesota",
        value: "MN"
    },
    {
        label: "Mississippi",
        value: "MS"
    },
    {
        label: "Missouri",
        value: "MO"
    },
    {
        label: "Montana",
        value: "MT"
    },
    {
        label: "Nebraska",
        value: "NE"
    },
    {
        label: "Nevada",
        value: "NV"
    },
    {
        label: "New Hampshire",
        value: "NH"
    },
    {
        label: "New Jersey",
        value: "NJ"
    },
    {
        label: "New Mexico",
        value: "NM"
    },
    {
        label: "New York",
        value: "NY"
    },
    {
        label: "North Carolina",
        value: "NC"
    },
    {
        label: "North Dakota",
        value: "ND"
    },
    {
        label: "Northern Mariana Islands",
        value: "MP"
    },
    {
        label: "Ohio",
        value: "OH"
    },
    {
        label: "Oklahoma",
        value: "OK"
    },
    {
        label: "Oregon",
        value: "OR"
    },
    {
        label: "Palau",
        value: "PW"
    },
    {
        label: "Pennsylvania",
        value: "PA"
    },
    {
        label: "Puerto Rico",
        value: "PR"
    },
    {
        label: "Rhode Island",
        value: "RI"
    },
    {
        label: "South Carolina",
        value: "SC"
    },
    {
        label: "South Dakota",
        value: "SD"
    },
    {
        label: "Tennessee",
        value: "TN"
    },
    {
        label: "Texas",
        value: "TX"
    },
    {
        label: "Utah",
        value: "UT"
    },
    {
        label: "Vermont",
        value: "VT"
    },
    {
        label: "Virgin Islands",
        value: "VI"
    },
    {
        label: "Virginia",
        value: "VA"
    },
    {
        label: "Washington",
        value: "WA"
    },
    {
        label: "West Virginia",
        value: "WV"
    },
    {
        label: "Wisconsin",
        value: "WI"
    },
    {
        label: "Wyoming",
        value: "WY"
    }
];

// Provinces where Spot CA currently operates. Alphabetized by label for the dropdown.
export const CA_PROVINCES: State[] = [
    {
        label: "Alberta",
        value: "AB"
    },
    {
        label: "British Columbia",
        value: "BC"
    },
    {
        label: "Manitoba",
        value: "MB"
    },
    {
        label: "New Brunswick",
        value: "NB"
    },
    {
        label: "Nova Scotia",
        value: "NS"
    },
    {
        label: "Ontario",
        value: "ON"
    },
    {
        label: "Prince Edward Island",
        value: "PE"
    },
    {
        label: "Saskatchewan",
        value: "SK"
    }
];

export const US_FOOTER_LINKS = [
    { label: "Terms & Conditions", href: `${PublicConfig.PTZ_US.SPOT_MARKETING_URL}/terms-conditions` },
    { label: "Privacy", href: `${PublicConfig.PTZ_US.SPOT_MARKETING_URL}/privacy` },
    { label: "Agency Information", href: `${PublicConfig.PTZ_US.SPOT_MARKETING_URL}/agency-info` },
    { label: "Sample Policy", href: `${PublicConfig.PTZ_US.SPOT_MARKETING_URL}/sample-policy` },
    { label: "ADA Notice", href: `${PublicConfig.PTZ_US.SPOT_MARKETING_URL}/ada-compliance-notice` },
    { label: "Sitemap", href: `${PublicConfig.PTZ_US.SPOT_MARKETING_URL}/sitemap.xml` }
];

export const US_POLICY_FACTS = [
    "You can use any licensed veterinarian in the US or Canada, including specialists and emergency vets!",
    "When submitting a claim, include your itemized vet bill and medical records for quicker reimbursement!",
    "Access the 24/7 Pet Telehealth Helpline in your Member Center to call or chat with a veterinary professional."
];

export const ACH_KEYWORDS = ["ebtp"];

export const THEMES = {
    DEFAULT: "default",
    DARK: "dark",
    REBRAND: "rebrand"
} as const;

export type Theme = (typeof THEMES)[keyof typeof THEMES];

export const ADD_PET_MODAL_TRIGGERS = {
    STICKY_MENU: "sticky-nav-cta",
    PET_CARD_CTA: "pet-card-cta",
    ALT_SUBMIT_CTA: "alt-submit-cta",
    COVERAGE_BODY_CTA: "coverage-body-cta"
} as const;

export type AddPetModalState = (typeof ADD_PET_MODAL_TRIGGERS)[keyof typeof ADD_PET_MODAL_TRIGGERS] | undefined;

/**
 * ENG-17301 — values of the `showCheckoutBtn` Statsig layer parameter, which adds a
 * "Proceed to Checkout" CTA to the coverage step. Anything else (including the `""` default the
 * control group resolves to) means no test CTA.
 */
export const CHECKOUT_CTA_VARIANTS = {
    /** Variant 1 — swaps the sticky menu's "Add a Pet" button for the checkout button. */
    STICKY_MENU: "inStickyMenu",
    /** Variant 2 — pins the checkout button to the bottom of the viewport. */
    FLOATING: "floating"
} as const;

export type CheckoutCtaVariant = (typeof CHECKOUT_CTA_VARIANTS)[keyof typeof CHECKOUT_CTA_VARIANTS];

export const isCheckoutCtaVariant = (value?: string): value is CheckoutCtaVariant => value === CHECKOUT_CTA_VARIANTS.STICKY_MENU || value === CHECKOUT_CTA_VARIANTS.FLOATING;
